import { Component, computed, inject, OnInit, Signal, signal, WritableSignal } from '@angular/core';
import { Router } from '@angular/router';
import { ButtonCtaComponent } from '@components/button-cta/button-cta.component';
import { CsIconModule } from '@components/icon';
import { BroadcastService } from '@services/broadcast-service';
import { ConnectionService } from '@services/connection-service';
import { EventLoggerService } from '@services/event-logger-service';
import { ExperimentService } from '@services/experiment/experiment.service';
import { LocalStorageService } from '@services/local-storage-service';
import { UserService } from '@services/user';
import { AppConfig } from '../../../app.config';
import { ONBOARDING_PROFILE_ROUTES } from '../onboarding-profile.constants';
import { OnboardingProfileFieldErrorComponent } from '../onboarding-profile-field-error/onboarding-profile-field-error.component';
import { OnboardingProfileShellComponent } from '../onboarding-profile-shell/onboarding-profile-shell.component';
import { OnboardingGenderCardComponent } from './onboarding-gender-card.component';

/** Step 1 of the split profile flow. Under `skip_gender_cta`, tapping a gender saves and advances. */
@Component({
  selector: 'app-onboarding-profile-gender',
  templateUrl: './onboarding-profile-gender.component.html',
  imports: [
    OnboardingProfileShellComponent,
    OnboardingGenderCardComponent,
    OnboardingProfileFieldErrorComponent,
    ButtonCtaComponent,
    CsIconModule,
  ],
})
export class OnboardingProfileGenderComponent implements OnInit {
  private readonly conn: ConnectionService = inject(ConnectionService);
  private readonly broadcast: BroadcastService = inject(BroadcastService);
  private readonly router: Router = inject(Router);
  private readonly storage: LocalStorageService = inject(LocalStorageService);
  private readonly eventLogger: EventLoggerService = inject(EventLoggerService);
  private readonly experimentService: ExperimentService = inject(ExperimentService);
  private readonly userService: UserService = inject(UserService);
  protected readonly appConfig: AppConfig = inject(AppConfig);

  protected readonly gender: WritableSignal<string> = signal('');
  protected readonly loading: WritableSignal<boolean> = signal(false);
  protected readonly showGenderError: WritableSignal<boolean> = signal(false);
  private readonly skipCta: WritableSignal<boolean> = signal(false);

  protected readonly isFemaleSelected: Signal<boolean> = computed(
    (): boolean => this.gender() === this.appConfig.Shared.Gender.FEMALE,
  );

  protected readonly isMaleSelected: Signal<boolean> = computed(
    (): boolean => this.gender() === this.appConfig.Shared.Gender.MALE,
  );

  private readonly hasSelection: Signal<boolean> = computed(
    (): boolean => this.isFemaleSelected() || this.isMaleSelected(),
  );

  protected readonly showFooter: Signal<boolean> = computed(
    (): boolean => !this.skipCta() && this.hasSelection(),
  );

  protected readonly isDravidianScriptUser: Signal<boolean> = computed(
    (): boolean => this.userService.dravidianScripts.includes(this.conn.getActingUser()?.get('languagePreference')),
  );

  async ngOnInit(): Promise<void> {
    await this.experimentService.experimentsLoaded;
    this.skipCta.set(this.experimentService.shouldSkipGenderCta());
    this.gender.set(this.conn.getActingUser()?.get('Gender') || this.storage.getValue('CureSkin/gender') || '');
    await this.conn.logPrivacyPolicyDate();
  }

  protected async selectGender(gender: string): Promise<void> {
    this.gender.set(gender);
    this.showGenderError.set(false);
    if (this.skipCta()) await this.saveAndContinue();
  }

  protected async handleContinue(): Promise<void> {
    if (!this.hasSelection()) {
      this.showGenderError.set(true);
      this.eventLogger.trackEvent('gender_not_selected', {});
      this.eventLogger.setUserProperty({ people_gender: 'NotSelected' });
      return;
    }
    await this.saveAndContinue();
  }

  private async saveAndContinue(): Promise<void> {
    if (this.loading()) return;
    this.loading.set(true);
    const gender = this.gender();
    this.eventLogger.trackEvent('gender_selected', { gender });
    this.eventLogger.setUserProperty({ people_gender: gender });
    try {
      await this.conn.updateUserData({ Gender: gender });
    } catch (error) {
      this.broadcast.broadcast('NOTIFY', { message: String(error) });
      return;
    } finally {
      this.loading.set(false);
    }
    await this.router.navigate([ONBOARDING_PROFILE_ROUTES.NAME]);
  }
}
